import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// The composer's Check button (report 4aaa018b): "a check button for users to be able to send an email or a reply to an email to
// their supervisors". The small menu lists who this writer may ask (server: services/emailCheck.ts, never the writer) plus Either,
// which is any of them. Two or more people make Either worth offering; with one, that one is the only row.
export default function CheckSendMenu({ disabled = false, onPick }: { disabled?: boolean; onPick: (reviewer: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<{ token: string; name: string }[] | null>(null);
  const [place, setPlace] = useState<{ up: boolean; left: boolean }>({ up: true, left: true });
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (!open) {
      setPeople(null);
      api.emailCheckSupervisors().then(d => setPeople(d.rows || [])).catch(() => setPeople([]));
      // The bar is at the screen's foot on a desktop and at its head in the phone's Email app, so the menu opens the way there is room.
      const r = boxRef.current?.getBoundingClientRect();
      if (r) setPlace({ up: r.top > 220, left: r.left + 240 <= window.innerWidth });
    }
    setOpen(v => !v);
  };
  const pick = (reviewer: string) => { setOpen(false); onPick(reviewer); };
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' };
  const hover = { onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'var(--bg-active)'), onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'transparent') };

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
      <button onClick={toggle} disabled={disabled} title={t('eml_check_menu')} aria-label={t('eml_check_menu')} aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: open ? 'var(--bg-active)' : 'transparent', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1 }}>
        <i className="ti ti-circle-check" style={{ fontSize: 13 }} aria-hidden />
        {t('eml_check_btn')}
      </button>
      {open && (
        <div style={{ position: 'absolute', ...(place.up ? { bottom: '100%', marginBottom: 6 } : { top: '100%', marginTop: 6 }), ...(place.left ? { left: 0 } : { right: 0 }), background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, minWidth: 220, zIndex: 60 }}>
          <div style={{ padding: '4px 9px 6px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>{t('eml_check_menu')}</div>
          {people === null && <div style={{ padding: '7px 9px', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('bf_loading')}</div>}
          {people !== null && people.length === 0 && <div style={{ padding: '7px 9px', fontSize: 12, color: 'var(--text-tertiary)', maxWidth: 240 }}>{t('eml_check_nobody')}</div>}
          {(people || []).map(p => (
            <button key={p.token} onClick={() => pick(p.token)} style={rowStyle} {...hover}>{p.name}</button>
          ))}
          {people !== null && people.length > 1 && (
            <>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button onClick={() => pick('any')} style={rowStyle} {...hover}>{t('eml_check_either')}</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
